import React, { useState, useEffect, useCallback } from 'react';
import { formatDecimal, parseCurrency } from '../../../../utils/formatters';
import { Button } from '../../../../components/ui/Button';
import Modal from '../../../../components/ui/Modal';
import Input from '../../../../components/ui/Input';

interface WinDealModalProps {
    isOpen: boolean;
    leadName: string;
    currentValue: number;
    onClose: () => void;
    onConfirm: (finalValue: number) => Promise<void>;
}

export const WinDealModal: React.FC<WinDealModalProps> = ({
    isOpen,
    leadName,
    currentValue,
    onClose,
    onConfirm
}) => {
    const [inputValue, setInputValue] = useState<string>('0,00');
    const [error, setError] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const formatted = currentValue.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            setInputValue(formatted);
            setError('');
        }
    }, [isOpen, currentValue]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const numericValue = value.replace(/\D/g, '');

        if (numericValue === '') {
            setInputValue('0,00');
            return;
        }

        const cleanVal = numericValue ? (parseFloat(numericValue) / 100) : 0;
        const formatted = formatDecimal(cleanVal);
        setInputValue(formatted);
        setError('');
    }, []);

    const handleConfirmWithCurrentValue = useCallback(async () => {
        if (currentValue <= 0) {
            setError('O valor da negociação não pode ser zero.');
            return;
        }
        setIsSaving(true);
        try {
            await onConfirm(currentValue);
        } finally {
            setIsSaving(false);
        }
    }, [currentValue, onConfirm]);

    const handleConfirmWithNewValue = useCallback(async () => {
        const numericValue = parseCurrency(inputValue);

        if (numericValue <= 0) {
            setError('O valor deve ser maior que zero.');
            return;
        }

        setIsSaving(true);
        try {
            await onConfirm(numericValue);
        } finally {
            setIsSaving(false);
        }
    }, [inputValue, onConfirm]);

    const handleClose = useCallback(() => {
        setInputValue('0,00');
        setError('');
        onClose();
    }, [onClose]);

    const formattedCurrentValue = currentValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    });

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="md">
            <Modal.Header onClose={handleClose}>
                Digite o valor do fechamento
            </Modal.Header>
            <Modal.Body className="space-y-6">
                {leadName && (
                    <div className="bg-surface-subtle p-3 rounded-lg border border-subtle">
                        <p className="text-sm text-secondary">
                            Negociação: <span className="font-semibold text-default">{leadName}</span>
                        </p>
                    </div>
                )}

                <div>
                    <Input
                        inputSize="lg"
                        variant="filled" // Destaque visual
                        icon={<span className="text-muted font-medium text-sm">R$</span>}
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder="0,00"
                        error={error}
                        helperText={currentValue > 0 ? `Valor original da negociação: ${formattedCurrentValue}` : undefined}
                        autoFocus
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                {currentValue > 0 && (
                    <Button
                        variant="outline"
                        onClick={handleConfirmWithCurrentValue}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Salvando...' : 'Manter valor original'}
                    </Button>
                )}
                <Button
                    onClick={handleConfirmWithNewValue}
                    className="min-w-[140px]"
                    disabled={isSaving}
                >
                    {isSaving ? 'Salvando...' : 'Salvar novo valor'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
